import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Progress,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { observer } from 'mobx-react';
import { MdFileDownload } from 'react-icons/md';

import UploadArea from './components/UploadArea';
import PlatformSelector from './components/PlatformSelector';
import AdvancedSettings from './components/AdvancedSettings';
import PreviewGrid from './components/PreviewGrid';
import DownloadPanel from './components/DownloadPanel';
import { appIconMakerStore } from './stores/appIconMakerStore';
import { sendEvent } from '../../common/analytics';

const AppIconMaker = observer(() => {
  const store = appIconMakerStore;

  const panelBorder = useColorModeValue('gray.200', 'gray.700');
  const leftBg = useColorModeValue('gray.50', 'gray.800');
  const rightBg = useColorModeValue('white', 'gray.900');
  const cardShadow = useColorModeValue('sm', 'none');
  const generateBtnBg = store.canGenerate ? 'blue.500' : undefined;

  useEffect(() => {
  }, []);

  const handleGenerate = async () => {
    sendEvent('AppIconMaker', 'Generate', store.selectedPlatforms.size.toString());
    await store.generate();
  };

  const hasGenerated = store.generatedIcons.length > 0;

  return (
    <Box maxW="960px" mx="auto">
      {/* ── Main card: 2-column layout ── */}
      <Box
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="2xl"
        overflow="hidden"
        shadow={cardShadow}
      >
        <Flex direction={{ base: 'column', md: 'row' }} align="stretch">
          {/* ── Left: Upload area ── */}
          <Box
            w={{ base: '100%', md: '40%' }}
            bg={leftBg}
            p={6}
            borderRightWidth={{ md: '1px' }}
            borderBottomWidth={{ base: '1px', md: '0' }}
            borderColor={panelBorder}
          >
            <UploadArea store={store} />
          </Box>

          {/* ── Right: Options + Generate ── */}
          <Box flex={1} bg={rightBg} p={6} display="flex" flexDirection="column">
            {/* Title */}
            <Text fontSize="xl" fontWeight="bold" mb={1}>
              App Icon Maker
            </Text>
            <Text fontSize="sm" color="gray.500" mb={5}>
              Drag or select an image (1024×1024) to generate all required icon sizes for every platform
            </Text>

            {/* Platform checkboxes */}
            <PlatformSelector store={store} />

            {/* Advanced settings */}
            <Box mt={1} mb={4}>
              <AdvancedSettings store={store} />
            </Box>

            {/* Spacer to push button to bottom */}
            <Box flex={1} />

            {/* Generate button */}
            <Button
              w="full"
              size="lg"
              colorScheme="blue"
              bg={generateBtnBg}
              leftIcon={<Icon as={MdFileDownload} />}
              isDisabled={!store.canGenerate || store.isGenerating}
              isLoading={store.isGenerating}
              loadingText={`Generating… ${store.progress}%`}
              onClick={handleGenerate}
              borderRadius="lg"
              fontWeight="semibold"
            >
              Generate
            </Button>

            {/* Progress bar */}
            {store.isGenerating && (
              <Progress
                mt={3}
                value={store.progress}
                size="xs"
                colorScheme="blue"
                borderRadius="full"
                hasStripe
                isAnimated
              />
            )}

            {/* Error */}
            {store.error && (
              <Box mt={3} p={3} borderRadius="lg" bg="red.50" borderWidth="1px" borderColor="red.200">
                <Text fontSize="sm" color="red.600">{store.error}</Text>
              </Box>
            )}
          </Box>
        </Flex>
      </Box>

      {/* ── Preview + Download (shown after generation) ── */}
      {hasGenerated && (
        <Box mt={6}>
          <DownloadPanel store={store} />
          <Box mt={5}>
            <Divider mb={4} />
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="gray.400" mb={3}>
              Preview
            </Text>
            <PreviewGrid store={store} />
          </Box>
        </Box>
      )}

    </Box>
  );
});

export default AppIconMaker;
